import './DodSchedulePicker.scss';
import React, {useState,useEffect} from 'react';
import {DodSchedule, DodScheduleDeliveryDay, DodScheduleFrequency} from '@/types/DodRun';
import {
    ByzzerChangeEventHandler,
    ByzzerSelect,
    ByzzerSelectOption,
    ByzzerTipIcon,
    ByzzerTextInput,
    ByzzerSelectValueTypes,
} from '@byzzer/ui-components';
import {arrayRange} from '@/utils/utils';
import {useDodTimePeriodService} from "@/services/dodTimePeriod.service";
import {useUser} from '@/contexts/UserContext';
import classnames from "classnames";
import {TimePeriod} from '@/utils/timePeriod/TimePeriod';
import {addDays, format} from 'date-fns';

const baseClassName = 'dod-schedule-picker';

export type DodSchedulePickerProps = {
    className?: string;
    name?: string;
    value: DodSchedule;
    initialSchedule?: DodSchedule;
    seriesRunCounts?: {
        completed: number;
        scheduled: number;
        total: number;
    };
    enableSeriesRunCounts?: boolean;
    isEditScheduleMode?: boolean;
    onFutureDeliveriesChange?(futureDeliveries: number): void;
    onChange: ByzzerChangeEventHandler<DodSchedule>;
    onValidityChange?(e: ByzzerValidityChangeEvent): void;
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

type FrequencyOption = {
    display: string;
    value: DodScheduleFrequency;
};

export enum FrequencyValueLabelMapping {
    one_time = 'One-Time',
    weekly = 'Weekly',
    monthly_444 = 'Monthly (4-4-4)',
    monthly_445 = 'Monthly (4-4-5)',
    quarterly = 'Quarterly',
    custom = 'Custom',
}

const FREQUENCY_OPTIONS: FrequencyOption[] = [
    {
        display: 'One-Time',
        value: 'one_time',
    },
    {
        display: 'Weekly',
        value: 'weekly',
    },
    {
        display: 'Monthly (4-4-4 Cadence)',
        value: 'monthly_444',
    },
    {
        display: 'Monthly (4-4-5 Cadence)',
        value: 'monthly_445',
    },
    {
        display: 'Quarterly',
        value: 'quarterly',
    },
    {
        display: 'Custom',
        value: 'custom',
    },
];

type DeliveryDayOption = {
    display: string;
    value: DodScheduleDeliveryDay;
};

const DELIVERY_DAY_OPTIONS: DeliveryDayOption[] = [
    {
        display: 'Tuesday Of Availability',
        value: 'tuesday'
    },
    {
        display: 'Wednesday Following Availability',
        value: 'wednesday',
    },
    {
        display: 'Thursday Following Availability',
        value: 'thursday',
    },
    {
        display: 'Friday Following Availability',
        value: 'friday',
    },
    {
        display: 'Saturday Following Availability',
        value: 'saturday',
    },
    {
        display: 'Sunday Following Availability',
        value: 'sunday',
    },
    {
        display: 'Monday Following Availability',
        value: 'monday',
    },
];

const MAX_DELIVERIES_WARNING = 'You can only schedule runs for up to 5 years in the future. Please enter a lower number of deliveries.';

const FIVE_YEAR_DELIVERY_LIMITS: Partial<Record<DodScheduleFrequency, number>> = {
    weekly: 260,
    monthly_444: 65,
    monthly_445: 60,
    quarterly: 20,
};

const deliveryDays: DodScheduleDeliveryDay[] = ['tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'monday'];

export function DodSchedulePicker({
                                      className,
                                      name,
                                      value,
                                      initialSchedule,
                                      seriesRunCounts,
                                      enableSeriesRunCounts = false,
                                      isEditScheduleMode = false,
                                      onFutureDeliveriesChange,
                                      onChange,
                                      onValidityChange,
                                      ...props
                                  }: DodSchedulePickerProps) {

    const {
        futureQuarterlyOptions,
        futureWeeklyOptions,
        future444Options,
        future445Options,
        maxFutureWeeks
    } = useDodTimePeriodService();
    const {subscription} = useUser();
    const [deliveryOptions, setDeliveryOptions] = useState<ByzzerSelectOption[]>(() => arrayRange(1, maxFutureWeeks, 1).map((entry) => ({
        display: `${entry}`,
        value: entry,
    })));
    const [startPeriodOptions, setStartPeriodOptions] = useState<ByzzerSelectOption[]>(futureWeeklyOptions);
    const [durationInput, setDurationInput] = useState<string>(value?.duration ? `${value.duration}` : '');
    const [durationInputError, setDurationInputError] = useState<string>('');

    useEffect(() => {
        const frequency = value?.frequency;
        switch (frequency) {
            case 'quarterly':
                setStartPeriodOptions(futureQuarterlyOptions);
                break;
            case 'monthly_444':
                setStartPeriodOptions(future444Options);
                break;
            case 'monthly_445':
                setStartPeriodOptions(future445Options);
                break;
        }
    }, []);

    useEffect(() => {
        setDurationInput(value?.duration ? `${value.duration}` : '');
        setDurationInputError('');
    }, [value?.duration]);

    useEffect(() => {
        onValidityChange?.({
            name: 'duration',
            isValid: !durationInputError,
        });
    }, [durationInputError]);

    const setDeliveryOptionData = (range: Number) => {
        setDeliveryOptions(() => {
            return arrayRange(1, range, 1).map((entry) => ({
                display: `${entry}`,
                value: entry,
            }))
        })
    }
    function handleFrequencyChange(e: ByzzerChangeEvent<ByzzerSelectValueTypes | undefined>) {

        const frequency: DodScheduleFrequency = e.value as DodScheduleFrequency;
        let startingTimePeriod: string | undefined;
        let timePeriods: string[] | undefined;
        let noOfDeliveries = 1;
        switch (frequency) {
            case "one_time":
            case "weekly":
                setStartPeriodOptions(futureWeeklyOptions);
                startingTimePeriod = futureWeeklyOptions[0].value;
                noOfDeliveries = Math.min(52, maxFutureWeeks);
                setDeliveryOptionData(maxFutureWeeks);
                break;
            case "custom":
                setStartPeriodOptions(futureWeeklyOptions);
                timePeriods = [futureWeeklyOptions[0].value];
                break;
            case "quarterly":
                setStartPeriodOptions(futureQuarterlyOptions);
                startingTimePeriod = futureQuarterlyOptions[0].value;
                noOfDeliveries = Math.min(4, futureQuarterlyOptions.length);

                setDeliveryOptionData(futureQuarterlyOptions.length);
                break;
            case "monthly_444":
                setStartPeriodOptions(future444Options);
                startingTimePeriod = future444Options[0].value;
                noOfDeliveries = Math.min(13, future444Options.length);
                setDeliveryOptionData(future444Options.length);
                break;
            case "monthly_445":
                setStartPeriodOptions(future445Options);
                startingTimePeriod = future445Options[0].value;
                noOfDeliveries = Math.min(12, future445Options.length);
                setDeliveryOptionData(future445Options.length);
                break;
        }
        onChange?.({
            value: {
                ...value,
                frequency,
                duration: noOfDeliveries,
                startingTimePeriod,
                timePeriods
            },
        });
    }

    function handleDeliveryDayChange(e: ByzzerChangeEvent<ByzzerSelectValueTypes | undefined>) {

        onChange?.({
            value: {
                ...value,
                deliveryDay: e.value as DodScheduleDeliveryDay,
            },
        });
    }

    function handleDurationChange(e: ByzzerChangeEvent<ByzzerSelectValueTypes | undefined>) {
        const duration: number = e.value as number;
        onChange?.({
            value: {
                ...value,
                duration
            },
        });
    }

    function handleDurationInputChange(e: ByzzerChangeEvent<string>) {
        const nextValue = (e.value || '').trim();
        const fiveYearLimit = FIVE_YEAR_DELIVERY_LIMITS[value.frequency] || Number.MAX_SAFE_INTEGER;
        const optionLimitByFrequency: Record<string, number> = {
            weekly: maxFutureWeeks,
            monthly_444: future444Options.length,
            monthly_445: future445Options.length,
            quarterly: futureQuarterlyOptions.length,
        };
        const optionLimit = optionLimitByFrequency[value.frequency] || Number.MAX_SAFE_INTEGER;
        const maxAllowedDeliveries = Math.min(fiveYearLimit, optionLimit);

        if (nextValue && !/^\d+$/.test(nextValue)) {
            setDurationInputError('Please enter a whole number.');
            return;
        }

        setDurationInput(nextValue);

        if (!nextValue) {
            setDurationInputError('Number of deliveries is required.');
            return;
        }

        const duration = Number(nextValue);
        if (!Number.isInteger(duration) || duration < 1) {
            setDurationInputError('Please enter a whole number greater than 0.');
            return;
        }

        if (duration > maxAllowedDeliveries) {
            setDurationInputError(MAX_DELIVERIES_WARNING);
            return;
        }

        setDurationInputError('');
        onChange?.({
            value: {
                ...value,
                duration,
            },
        });
    }

    function handleStartingTimePeriodChange(e: ByzzerChangeEvent<ByzzerSelectValueTypes | undefined>) {

        onChange?.({
            value: {
                ...value,
                startingTimePeriod: e.value as string,
                timePeriods: undefined
            },
        });
    }

    function handleTimePeriodsChange(e: ByzzerChangeEvent<ByzzerSelectValueTypes | undefined>) {

        onChange?.({
            value: {
                ...value,
                startingTimePeriod: undefined,
                timePeriods: e.value as string[]
            },
        });
    }
function getScheduleDeliveryCount(schedule?: DodSchedule): number {
        if (!schedule) {
            return 0;
        }

        if (schedule.frequency === 'one_time') {
            return 1;
        }

        if (schedule.frequency === 'custom') {
            return schedule.timePeriods?.length || 0;
        }

        return schedule.duration || 0;
    }

    function getFutureDeliveriesCount(params: {
        originalDeliveries: number;
        completedDeliveries: number;
        scheduledDeliveries: number;
        editedDeliveries: number;
    }): number {
        const {originalDeliveries, completedDeliveries, scheduledDeliveries, editedDeliveries} = params;

        if (completedDeliveries === 0) {
            return editedDeliveries;
        }

        if (editedDeliveries <= scheduledDeliveries) {
            return editedDeliveries;
        }

        if (editedDeliveries <= originalDeliveries) {
            return scheduledDeliveries;
        }

        return editedDeliveries - completedDeliveries;
    }

    const oldNumberOfDeliveries = getScheduleDeliveryCount(initialSchedule);
    const isSeriesCountsEnabled = enableSeriesRunCounts;
    const completedRunsCount = Math.max(0, seriesRunCounts?.completed || 0);
    const scheduledRunsCount = Math.max(0, seriesRunCounts?.scheduled || 0);
    const originalScheduleDeliveries = isSeriesCountsEnabled
        ? Math.max(0, oldNumberOfDeliveries || Math.max(0, completedRunsCount + scheduledRunsCount))
        : Math.max(0, oldNumberOfDeliveries);
    const runsAlreadyDelivered = isSeriesCountsEnabled ? completedRunsCount : Math.max(0, oldNumberOfDeliveries);
    const nextDurationInputValue = durationInput === '' ? 0 : Number(durationInput);
    const newNumberOfDeliveries = !['custom', 'one_time'].includes(value.frequency)
        ? (Number.isNaN(nextDurationInputValue) ? 0 : nextDurationInputValue)
        : getScheduleDeliveryCount(value);
    const futureDeliveries = isSeriesCountsEnabled
        ? Math.max(0, getFutureDeliveriesCount({
            originalDeliveries: originalScheduleDeliveries,
            completedDeliveries: completedRunsCount,
            scheduledDeliveries: scheduledRunsCount,
            editedDeliveries: newNumberOfDeliveries,
        }))
        : Math.max(0, newNumberOfDeliveries - oldNumberOfDeliveries);

    useEffect(() => {
        if (!isEditScheduleMode) {
            return;
        }

        onFutureDeliveriesChange?.(futureDeliveries);
    }, [futureDeliveries, isEditScheduleMode, onFutureDeliveriesChange]);

    const selectedFrequencyLabel = FREQUENCY_OPTIONS.find((option) => option.value === value.frequency)?.display || value.frequency;
    const selectedDeliveryDayLabel = (DELIVERY_DAY_OPTIONS.find((option) => option.value === value.deliveryDay)?.display || value.deliveryDay)
        ?.split(' ')[0];
    const subscriptionEndDate = subscription?.endDate ? new Date(subscription.endDate) : undefined;

    const getScheduledDeliveryDates = (schedule: DodSchedule): Date[] => {
        const {duration = 1, frequency, startingTimePeriod, deliveryDay} = schedule;
        if (!frequency || !startingTimePeriod || !deliveryDay) {
            return [];
        }

        let availablePeriods: string[] = [];
        switch (frequency) {
            case 'weekly':
                availablePeriods = futureWeeklyOptions.map((option) => option.value);
                break;
            case 'quarterly':
                availablePeriods = futureQuarterlyOptions.map((option) => option.value);
                break;
            case 'monthly_444':
                availablePeriods = future444Options.map((option) => option.value);
                break;
            case 'monthly_445':
                availablePeriods = future445Options.map((option) => option.value);
                break;
            default:
                return [];
        }

        const firstTimePeriod = new TimePeriod(startingTimePeriod);
        const startIndex = availablePeriods.findIndex((period) => period === firstTimePeriod.toString());
        if (startIndex === -1) {
            return [];
        }

        const deliveryOffset = deliveryDays.indexOf(deliveryDay) + 10;
        return availablePeriods
            .slice(startIndex, startIndex + duration)
            .map((period) => addDays(new TimePeriod(period).toGmtDate(), deliveryOffset));
    };

    const scheduledDeliveryDates = !['custom', 'one_time'].includes(value.frequency)
        ? getScheduledDeliveryDates(value)
        : [];
    const deliveriesBeforeExpiryCount = subscriptionEndDate && !Number.isNaN(subscriptionEndDate.getTime())
        ? scheduledDeliveryDates.filter((deliveryDate) => deliveryDate <= subscriptionEndDate).length
        : scheduledDeliveryDates.length;
    const expiresBeforeAllDeliveries = Boolean(subscriptionEndDate && !Number.isNaN(subscriptionEndDate.getTime())
        && deliveriesBeforeExpiryCount < scheduledDeliveryDates.length);
    const formattedSubscriptionEndDate = subscriptionEndDate && !Number.isNaN(subscriptionEndDate.getTime())
        ? format(subscriptionEndDate, 'MM/dd/yy')
        : '';
    const baseFutureDeliveriesTip = `You have ${futureDeliveries} upcoming deliveries in this ${selectedFrequencyLabel} series that will deliver on ${selectedDeliveryDayLabel}s`;
    const expiryFutureDeliveriesTip = deliveriesBeforeExpiryCount === 0
        ? `Your subscription expires on ${formattedSubscriptionEndDate}, so we cannot deliver any of your scheduled runs. Please renew your subscription to receive all of your scheduled deliveries.`
        : `Your subscription expires on ${formattedSubscriptionEndDate}, so we can only deliver ${deliveriesBeforeExpiryCount} more of your scheduled runs. Please renew your subscription to receive all of your scheduled deliveries.`;
    const futureDeliveriesTip = expiresBeforeAllDeliveries
        ? `${baseFutureDeliveriesTip}. ${expiryFutureDeliveriesTip}`
        : baseFutureDeliveriesTip;
    const pastDeliveriesTip = 'This is the number of runs from this series that have already been delivered.';

    
    return (
        <div className={classnames(baseClassName, className)} {...props}>
            <ByzzerSelect
                allowClear={false}
                className={`${baseClassName}__frequency-picker`}
                label="Frequency"
                value={value.frequency}
                options={FREQUENCY_OPTIONS}
                onChange={handleFrequencyChange}
            />
            <ByzzerSelect
                allowClear={false}
                className={`${baseClassName}__delivery-day-picker`}
                label="Delivery On"
                value={value.deliveryDay}
                options={DELIVERY_DAY_OPTIONS}
                onChange={handleDeliveryDayChange}
            />
            {value.frequency !== 'custom' && (
                <ByzzerSelect
                    allowClear={false}
                    className={`${baseClassName}__weekending-day-picker`}
                    label="Starting with Data Week Ending"
                    value={value.startingTimePeriod}
                    options={startPeriodOptions}
                    onChange={handleStartingTimePeriodChange}
                />
            )}
            {value.frequency === 'custom' && (
                <ByzzerSelect
                    className={`${baseClassName}__weekending-day-picker`}
                    label="Starting with Data Week Ending"
                    value={value.timePeriods}
                    options={startPeriodOptions}
                    onChange={handleTimePeriodsChange}
                    allowMultiple
                />
            )}
            {!['custom', 'one_time'].includes(value.frequency) && (
                isEditScheduleMode ? (
                    <div className={`${baseClassName}__repetition-picker`}>
                        <div className={`${baseClassName}__repetition-input-row`}>
                            <ByzzerTextInput
                                className={`${baseClassName}__repetition-input`}
                                label="Number of Deliveries"
                                name="duration"
                                placeholder="Enter number of deliveries"
                                value={durationInput}
                                onChange={handleDurationInputChange}
                            />
                            {durationInputError && (
                                <span className={`${baseClassName}__repetition-warning`}>
                                    <ByzzerTipIcon type={'warning'} tip={durationInputError} tipDelay={[300, 0]} />
                                </span>
                            )}
                        </div>
                        <div className={`${baseClassName}__future-deliveries`}>
                            <span className={`${baseClassName}__future-deliveries-label`}>Number of Future Deliveries</span>
                            <span className={`${baseClassName}__future-deliveries-value`}>
                                <strong><em style={{ paddingRight: '5px' }}>{futureDeliveries}</em></strong> future deliveries
                                <span className={`${baseClassName}__future-deliveries-tip`}>
                                    <ByzzerTipIcon
                                        tip={futureDeliveriesTip}
                                        tipDelay={[300, 0]}
                                    />
                                </span>
                            </span>
                        </div>
                        {isSeriesCountsEnabled && (
                            <div className={`${baseClassName}__future-deliveries`}>
                                <span className={`${baseClassName}__future-deliveries-label`}>Number of Past Deliveries</span>
                                <span className={`${baseClassName}__future-deliveries-value`}>
                                    <strong><em style={{ paddingRight: '5px' }}>{runsAlreadyDelivered}</em></strong> runs delivered
                                    <span className={`${baseClassName}__future-deliveries-tip`}>
                                        <ByzzerTipIcon
                                            tip={pastDeliveriesTip}
                                            tipDelay={[300, 0]}
                                        />
                                    </span>
                                </span>
                            </div>
                        )}
                    </div>
                ) : (
                    <ByzzerSelect
                        allowClear={false}
                        className={`${baseClassName}__repetition-picker`}
                        label="Number of Deliveries"
                        value={value.duration}
                        options={deliveryOptions}
                        onChange={handleDurationChange}
                    />
                )
            )}
        </div>
    );
}

export default DodSchedulePicker;
